import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PromptTemplateListDTOType,
  StylingWorkflowDTOType,
  StylingWorkflowListItemDTOType,
  StylingWorkflowDraftDTOType,
  LookDTOType,
} from '../../schemas/styling';
import { FilterDTOType } from '../../schemas';
import type { RootState } from '../store';

interface StylistState {
  // Prompt Template state
  promptTemplateFilters: Array<FilterDTOType>;
  isDrawerOpen: boolean;
  currentTemplateId: number | null;
  currentTemplate: PromptTemplateListDTOType | null;

  // Styling Workflow state
  stylingWorkflowFilters: Array<FilterDTOType>;
  isWorkflowDrawerOpen: boolean;
  currentWorkflowId: number | null;
  currentWorkflow: StylingWorkflowDTOType | StylingWorkflowListItemDTOType | null;

  // Styling Workflow Creation state
  isStylingWorkflowDrawerOpen: boolean;
  stylingWorkflowCreatePayload: StylingWorkflowDraftDTOType | null;

  // Workflow Runs state (now Looks)
  workflowRunFilters: Array<FilterDTOType>;
  isRunDrawerOpen: boolean;
  currentRunId: number | null;
  currentRun: LookDTOType | null;

  // QC state
  qcFilters: Array<FilterDTOType>;
  isQCDrawerOpen: boolean;
  currentQCItemId: number | null;
  currentQCItem: LookDTOType | null;
  qcActiveTab: 'pending' | 'rejected';
}

const initialState: StylistState = {
  // Prompt Template state
  promptTemplateFilters: [],
  isDrawerOpen: false,
  currentTemplateId: null,
  currentTemplate: null,

  // Styling Workflow state
  stylingWorkflowFilters: [],
  isWorkflowDrawerOpen: false,
  currentWorkflowId: null,
  currentWorkflow: null,

  // Styling Workflow Creation state
  isStylingWorkflowDrawerOpen: false,
  stylingWorkflowCreatePayload: null,

  // Workflow Runs state
  workflowRunFilters: [],
  isRunDrawerOpen: false,
  currentRunId: null,
  currentRun: null,

  // QC state
  qcFilters: [],
  isQCDrawerOpen: false,
  currentQCItemId: null,
  currentQCItem: null,
  qcActiveTab: 'pending',
};

const stylistSlice = createSlice({
  name: 'stylist',
  initialState,
  reducers: {
    // Prompt Template actions
    addPromptTemplateFilter: (state, action: PayloadAction<FilterDTOType>) => {
      const existingIndex = state.promptTemplateFilters.findIndex(
        filter =>
          filter.field === action.payload.field && filter.filterType === action.payload.filterType,
      );

      if (existingIndex !== -1) {
        state.promptTemplateFilters[existingIndex] = action.payload;
      } else {
        state.promptTemplateFilters.push(action.payload);
      }
    },
    removePromptTemplateFilter: (
      state,
      action: PayloadAction<{ field: string; filterType: string }>,
    ) => {
      state.promptTemplateFilters = state.promptTemplateFilters.filter(
        filter =>
          !(
            filter.field === action.payload.field && filter.filterType === action.payload.filterType
          ),
      );
    },
    clearAllPromptTemplateFilters: state => {
      state.promptTemplateFilters = [];
    },
    setIsDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isDrawerOpen = action.payload;
    },
    setCurrentTemplateId: (state, action: PayloadAction<number | null>) => {
      state.currentTemplateId = action.payload;
    },
    setCurrentTemplate: (state, action: PayloadAction<PromptTemplateListDTOType | null>) => {
      state.currentTemplate = action.payload;
    },
    clearCurrentTemplate: state => {
      state.currentTemplate = null;
      state.currentTemplateId = null;
    },

    // Styling Workflow actions
    addStylingWorkflowFilter: (state, action: PayloadAction<FilterDTOType>) => {
      const existingIndex = state.stylingWorkflowFilters.findIndex(
        filter =>
          filter.field === action.payload.field && filter.filterType === action.payload.filterType,
      );

      if (existingIndex !== -1) {
        state.stylingWorkflowFilters[existingIndex] = action.payload;
      } else {
        state.stylingWorkflowFilters.push(action.payload);
      }
    },
    removeStylingWorkflowFilter: (
      state,
      action: PayloadAction<{ field: string; filterType: string }>,
    ) => {
      state.stylingWorkflowFilters = state.stylingWorkflowFilters.filter(
        filter =>
          !(
            filter.field === action.payload.field && filter.filterType === action.payload.filterType
          ),
      );
    },
    clearAllStylingWorkflowFilters: state => {
      state.stylingWorkflowFilters = [];
    },
    setStylingWorkflowFilters: (state, action: PayloadAction<Array<FilterDTOType>>) => {
      state.stylingWorkflowFilters = action.payload;
    },
    setIsWorkflowDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isWorkflowDrawerOpen = action.payload;
    },
    setCurrentWorkflowId: (state, action: PayloadAction<number | null>) => {
      state.currentWorkflowId = action.payload;
    },
    setCurrentWorkflow: (
      state,
      action: PayloadAction<StylingWorkflowDTOType | StylingWorkflowListItemDTOType | null>,
    ) => {
      state.currentWorkflow = action.payload;
    },
    clearCurrentWorkflow: state => {
      state.currentWorkflow = null;
      state.currentWorkflowId = null;
    },

    // Styling Workflow Creation actions
    setIsStylingWorkflowDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isStylingWorkflowDrawerOpen = action.payload;
    },
    updateStylingWorkflowCreatePayload: (
      state,
      action: PayloadAction<Partial<StylingWorkflowDraftDTOType>>,
    ) => {
      if (state.stylingWorkflowCreatePayload) {
        state.stylingWorkflowCreatePayload = {
          ...state.stylingWorkflowCreatePayload,
          ...action.payload,
        };
      } else {
        state.stylingWorkflowCreatePayload = action.payload as StylingWorkflowDraftDTOType;
      }
    },
    clearStylingWorkflowPayload: state => {
      state.stylingWorkflowCreatePayload = null;
    },

    // Workflow Run actions
    addWorkflowRunFilter: (state, action: PayloadAction<FilterDTOType>) => {
      const existingIndex = state.workflowRunFilters.findIndex(
        filter =>
          filter.field === action.payload.field && filter.filterType === action.payload.filterType,
      );

      if (existingIndex !== -1) {
        state.workflowRunFilters[existingIndex] = action.payload;
      } else {
        state.workflowRunFilters.push(action.payload);
      }
    },
    removeWorkflowRunFilter: (
      state,
      action: PayloadAction<{ field: string; filterType: string }>,
    ) => {
      state.workflowRunFilters = state.workflowRunFilters.filter(
        filter =>
          !(
            filter.field === action.payload.field && filter.filterType === action.payload.filterType
          ),
      );
    },
    clearAllWorkflowRunFilters: state => {
      state.workflowRunFilters = [];
    },
    setIsRunDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isRunDrawerOpen = action.payload;
    },
    setCurrentRunId: (state, action: PayloadAction<number | null>) => {
      state.currentRunId = action.payload;
    },
    setCurrentRun: (state, action: PayloadAction<LookDTOType | null>) => {
      state.currentRun = action.payload;
    },
    clearCurrentRun: state => {
      state.currentRun = null;
      state.currentRunId = null;
    },

    // QC actions
    addQCFilter: (state, action: PayloadAction<FilterDTOType>) => {
      const existingIndex = state.qcFilters.findIndex(
        filter =>
          filter.field === action.payload.field && filter.filterType === action.payload.filterType,
      );

      if (existingIndex !== -1) {
        state.qcFilters[existingIndex] = action.payload;
      } else {
        state.qcFilters.push(action.payload);
      }
    },
    removeQCFilter: (state, action: PayloadAction<{ field: string; filterType: string }>) => {
      state.qcFilters = state.qcFilters.filter(
        filter =>
          !(
            filter.field === action.payload.field && filter.filterType === action.payload.filterType
          ),
      );
    },
    clearAllQCFilters: state => {
      state.qcFilters = [];
    },
    setIsQCDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isQCDrawerOpen = action.payload;
    },
    setCurrentQCItemId: (state, action: PayloadAction<number | null>) => {
      state.currentQCItemId = action.payload;
    },
    setCurrentQCItem: (state, action: PayloadAction<LookDTOType | null>) => {
      state.currentQCItem = action.payload;
    },
    clearCurrentQCItem: state => {
      state.currentQCItem = null;
      state.currentQCItemId = null;
    },
    setQCActiveTab: (state, action: PayloadAction<'pending' | 'rejected'>) => {
      state.qcActiveTab = action.payload;
    },
  },
});

export const {
  // Prompt Template actions
  addPromptTemplateFilter,
  removePromptTemplateFilter,
  clearAllPromptTemplateFilters,
  setIsDrawerOpen,
  setCurrentTemplateId,
  setCurrentTemplate,
  clearCurrentTemplate,

  // Styling Workflow actions
  addStylingWorkflowFilter,
  removeStylingWorkflowFilter,
  clearAllStylingWorkflowFilters,
  setStylingWorkflowFilters,
  setIsWorkflowDrawerOpen,
  setCurrentWorkflowId,
  setCurrentWorkflow,
  clearCurrentWorkflow,

  // Styling Workflow Creation actions
  setIsStylingWorkflowDrawerOpen,
  updateStylingWorkflowCreatePayload,
  clearStylingWorkflowPayload,

  // Workflow Run actions
  addWorkflowRunFilter,
  removeWorkflowRunFilter,
  clearAllWorkflowRunFilters,
  setIsRunDrawerOpen,
  setCurrentRunId,
  setCurrentRun,
  clearCurrentRun,

  // QC actions
  addQCFilter,
  removeQCFilter,
  clearAllQCFilters,
  setIsQCDrawerOpen,
  setCurrentQCItemId,
  setCurrentQCItem,
  clearCurrentQCItem,
  setQCActiveTab,
} = stylistSlice.actions;

// Selectors
export const selectPromptTemplateFilters = (state: RootState) =>
  state.stylist.promptTemplateFilters;
export const selectIsDrawerOpen = (state: RootState) => state.stylist.isDrawerOpen;
export const selectCurrentTemplateId = (state: RootState) => state.stylist.currentTemplateId;
export const selectCurrentTemplate = (state: RootState) => state.stylist.currentTemplate;

// Styling Workflow selectors
export const selectStylingWorkflowFilters = (state: RootState) =>
  state.stylist.stylingWorkflowFilters;
export const selectIsWorkflowDrawerOpen = (state: RootState) => state.stylist.isWorkflowDrawerOpen;
export const selectCurrentWorkflowId = (state: RootState) => state.stylist.currentWorkflowId;
export const selectCurrentWorkflow = (state: RootState) => state.stylist.currentWorkflow;

// Styling Workflow Creation selectors
export const selectIsStylingWorkflowDrawerOpen = (state: RootState) =>
  state.stylist.isStylingWorkflowDrawerOpen;
export const selectStylingWorkflowCreatePayload = (state: RootState) =>
  state.stylist.stylingWorkflowCreatePayload;

// Workflow Run selectors
export const selectWorkflowRunFilters = (state: RootState) => state.stylist.workflowRunFilters;
export const selectIsRunDrawerOpen = (state: RootState) => state.stylist.isRunDrawerOpen;
export const selectCurrentRunId = (state: RootState) => state.stylist.currentRunId;
export const selectCurrentRun = (state: RootState) => state.stylist.currentRun;

// QC selectors
export const selectQCFilters = (state: RootState) => state.stylist.qcFilters;
export const selectIsQCDrawerOpen = (state: RootState) => state.stylist.isQCDrawerOpen;
export const selectCurrentQCItemId = (state: RootState) => state.stylist.currentQCItemId;
export const selectCurrentQCItem = (state: RootState) => state.stylist.currentQCItem;
export const selectQCActiveTab = (state: RootState) => state.stylist.qcActiveTab;

export default stylistSlice.reducer;
